/**
 * Tier 2 of the design system: the SEMANTIC THEME (docs/architecture/DESIGN_SYSTEM.md §3).
 *
 * A component asks for a role ("the text on a primary action"), never a value, so changing what a
 * role looks like is one line here. Every text and fill pair a member draws meets WCAG AA, held by
 * `contrast.test.ts` (QD1, the user 2026-10-08).
 *
 * Pure data: no React. `useTheme()` (`./ThemeProvider`) hands it to components.
 */
import { amber, blackAlpha, blue, green, neutral, red, SHADOWS, SPACING, violet, yellow } from "./palette";

/** A filled or outlined action's colours, at rest and under a pointer or a finger. */
export interface ActionRole {
  bg: string;
  bgHover: string;
  bgPressed: string;
  fg: string;
  border: string;
}

/** A status: words in `fg` on `bg` (a subtle pill or notice), or `onSolid` on `solid`. */
export interface ToneRole {
  fg: string;
  bg: string;
  border: string;
  solid: string;
  onSolid: string;
}

export interface TypeRole {
  fontSize: number;
  lineHeight: number;
  fontWeight: "400" | "500" | "600" | "700";
}

export const TYPE_ROLES = [
  "display",
  "heading",
  "title",
  "bodyLarge",
  "body",
  "bodyStrong",
  "label",
  "caption",
  "micro",
] as const;
export type TypeRoleName = (typeof TYPE_ROLES)[number];

/**
 * `highlight` marks something special about a thing rather than how it is going: My Orders' preorder pill and its
 * large, scheduled and express order tags (MY_ORDERS_V2_PLAN.md §14.4, the user 2026-11-04: "add a purple role").
 */
export const STATUS_TONES = ["success", "warning", "danger", "info", "neutral", "highlight"] as const;
export type StatusTone = (typeof STATUS_TONES)[number];

export interface Theme {
  /** the cache key of every resolved look (DESIGN_SYSTEM.md §4.4): two themes never share an id */
  id: string;
  color: {
    /** `secondary` is also a field's placeholder */
    text: { primary: string; secondary: string; disabled: string; inverse: string; link: string };
    surface: {
      /** the grey a screen sits on */
      page: string;
      /** a card, a sheet, a field */
      raised: string;
      /** a quiet fill: a chip, the ADD button */
      muted: string;
      selected: string;
      /** a toast */
      inverse: string;
      scrim: string;
      /** state layers over any ground (black at 4 and 8 %) */
      hover: string;
      pressed: string;
    };
    border: { subtle: string; default: string; strong: string; focus: string; selected: string };
    action: { primary: ActionRole; secondary: ActionRole; danger: ActionRole };
    /** an offer or a reward the user has not taken yet */
    accent: { fg: string; bg: string; border: string };
    status: Record<StatusTone, ToneRole>;
    /** domain symbols more than one surface draws; their colours are set by convention, not contrast */
    mark: Record<"veg" | "nonVeg" | "egg", { fg: string; bg: string }>;
    shadow: string;
  };
  type: Record<TypeRoleName, TypeRole>;
  space: Record<keyof typeof SPACING, number>;
  radius: { control: number; card: number; sheet: number; pill: number };
  elevation: Record<keyof typeof SHADOWS, string>;
}

const type = (fontSize: number, lineHeight: number, fontWeight: TypeRole["fontWeight"]): TypeRole => ({
  fontSize,
  lineHeight,
  fontWeight,
});

const tone = (fg: string, bg: string, border: string, solid: string, onSolid: string): ToneRole => ({
  fg,
  bg,
  border,
  solid,
  onSolid,
});

/** The POS's theme: the default `useTheme()` answers with no provider mounted. */
export const posTheme: Theme = {
  id: "pos",
  color: {
    text: {
      primary: neutral[900],
      secondary: neutral[700],
      disabled: neutral[500],
      inverse: neutral[0],
      link: blue[700],
    },
    surface: {
      page: neutral[100],
      raised: neutral[0],
      muted: neutral[200],
      selected: blue[50],
      inverse: neutral[900],
      scrim: blackAlpha(0.5),
      hover: blackAlpha(0.04),
      pressed: blackAlpha(0.08),
    },
    border: {
      subtle: neutral[200],
      default: neutral[300],
      strong: neutral[600],
      focus: blue[500],
      selected: blue[500],
    },
    action: {
      primary: { bg: blue[600], bgHover: blue[700], bgPressed: blue[800], fg: neutral[0], border: blue[600] },
      secondary: { bg: neutral[0], bgHover: neutral[100], bgPressed: neutral[200], fg: neutral[900], border: neutral[300] },
      danger: { bg: red[600], bgHover: red[700], bgPressed: red[800], fg: neutral[0], border: red[600] },
    },
    accent: { fg: blue[700], bg: blue[50], border: blue[200] },
    status: {
      success: tone(green[700], green[50], green[200], green[700], neutral[0]),
      warning: tone(amber[800], amber[50], amber[200], amber[400], neutral[900]),
      danger: tone(red[700], red[50], red[200], red[600], neutral[0]),
      info: tone(blue[700], blue[50], blue[200], blue[600], neutral[0]),
      neutral: tone(neutral[800], neutral[100], neutral[300], neutral[800], neutral[0]),
      highlight: tone(violet[700], violet[50], violet[200], violet[700], neutral[0]),
    },
    mark: {
      veg: { fg: green[700], bg: green[50] },
      nonVeg: { fg: red[600], bg: red[50] },
      egg: { fg: yellow[500], bg: yellow[50] },
    },
    shadow: neutral[1000],
  },
  type: {
    display: type(24, 32, "700"),
    heading: type(20, 28, "600"),
    title: type(18, 24, "600"),
    bodyLarge: type(16, 24, "400"),
    body: type(14, 20, "400"),
    bodyStrong: type(14, 20, "600"),
    label: type(13, 18, "500"),
    caption: type(12, 16, "400"),
    micro: type(10, 14, "500"),
  },
  space: SPACING,
  radius: { control: 8, card: 12, sheet: 12, pill: 999 },
  elevation: SHADOWS,
};
